import { useEffect, useState } from 'react';
import type {
  MeetingResearchReport,
  MeetingResearchStatusApi,
  UpcomingMeeting,
} from '../../../lib/meetingsApi';
import {
  useMeetingResearch,
  useTriggerMeetingResearch,
  useUpcomingMeetings,
} from '../../../hooks/useMeetings';

interface Props {
  userId: string;
  date: string;
}

const STATUS_BADGE: Record<MeetingResearchStatusApi, { label: string; className: string }> = {
  none: { label: 'Fără research', className: 'bg-gray-100 text-gray-500' },
  pending: { label: 'În coadă…', className: 'bg-amber-100 text-amber-700' },
  in_progress: { label: 'Se cercetează…', className: 'bg-amber-100 text-amber-700' },
  done: { label: 'Gata', className: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Eșuat', className: 'bg-red-100 text-red-700' },
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ro-RO', { hour: 'numeric', minute: '2-digit' });
}

/** A bulleted list block with a heading; renders nothing when empty. */
function ListBlock({ title, items }: { title: string; items: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-3">
      <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
      <ul className="mt-1 list-inside list-disc space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-gray-700">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Renders the structured research report inside the modal. */
function ReportView({ report }: { report: MeetingResearchReport }) {
  return (
    <div>
      {report.unverified && (
        <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
          Research neverificat — generat fără căutare web live. Verifică sursele.
        </p>
      )}

      {report.tldr.length > 0 && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
          <h4 className="text-sm font-semibold text-gray-800">TL;DR</h4>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            {report.tldr.map((t, i) => (
              <li key={i} className="text-sm text-gray-700">
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.profile && (
        <div className="mt-3">
          <h4 className="text-sm font-semibold text-gray-800">Profil</h4>
          <p className="mt-1 whitespace-pre-line text-sm text-gray-700">{report.profile}</p>
        </div>
      )}

      {report.onlinePresence.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-semibold text-gray-800">Prezență online</h4>
          <ul className="mt-1 space-y-1">
            {report.onlinePresence.map((p, i) => (
              <li key={i} className="text-sm text-gray-700">
                <span className="font-medium">{p.platform}</span>
                {p.url && (
                  <>
                    {' · '}
                    <a
                      className="text-blue-600 underline"
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      link
                    </a>
                  </>
                )}
                {p.note ? ` — ${p.note}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.mediaAppearances.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-semibold text-gray-800">Apariții media</h4>
          <ul className="mt-1 space-y-1">
            {report.mediaAppearances.map((m, i) => (
              <li key={i} className="text-sm text-gray-700">
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                  {m.type}
                </span>{' '}
                {m.url ? (
                  <a
                    className="text-blue-600 underline"
                    href={m.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {m.title}
                  </a>
                ) : (
                  m.title
                )}
                {m.note ? ` — ${m.note}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ListBlock title="Briefing" items={report.recommendations.briefing} />
      <ListBlock title="Puncte de discuție" items={report.recommendations.talkingPoints} />
      <ListBlock title="Teren comun" items={report.recommendations.commonGround} />
      <ListBlock title="Atenționări" items={report.recommendations.cautions} />

      {report.sources.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-semibold text-gray-800">Surse</h4>
          <ul className="mt-1 space-y-0.5">
            {report.sources.map((s, i) => (
              <li key={i} className="truncate text-xs">
                <a className="text-blue-600 underline" href={s} target="_blank" rel="noreferrer">
                  {s}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Modal showing the research for one meeting. Triggers research on first open
 * when none exists, polls the status while a job is in flight, and renders the
 * full structured report when done.
 */
function ResearchModal({ meeting, onClose }: { meeting: UpcomingMeeting; onClose: () => void }) {
  const trigger = useTriggerMeetingResearch();
  // Poll while the (possibly just-triggered) job is pending/in_progress.
  const [polling, setPolling] = useState(
    meeting.researchStatus === 'pending' || meeting.researchStatus === 'in_progress',
  );
  const research = useMeetingResearch(meeting.eventId, polling);

  const status = research.data?.status ?? meeting.researchStatus;

  // Stop polling once the job reaches a terminal state.
  useEffect(() => {
    if (status === 'done' || status === 'failed' || status === 'none') {
      setPolling(false);
    }
    if (status === 'pending' || status === 'in_progress') {
      setPolling(true);
    }
  }, [status]);

  const handleTrigger = () => {
    setPolling(true);
    trigger.mutate(meeting.eventId, {
      onError: () => setPolling(false),
    });
  };

  const report = research.data?.research?.report ?? null;
  const isBusy = status === 'pending' || status === 'in_progress' || trigger.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{meeting.personName}</h3>
            <p className="text-xs text-gray-500">
              {meeting.meetingTitle} · {meeting.meetingDate}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            ✕
          </button>
        </div>

        {meeting.meetingDescription && (
          <p className="mb-3 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-600">
            {meeting.meetingDescription}
          </p>
        )}

        {isBusy && (
          <div className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
            Se caută informații despre {meeting.personName}…
          </div>
        )}

        {status === 'failed' && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            Research a eșuat
            {research.data?.research?.error ? `: ${research.data.research.error}` : '.'}
            <button
              type="button"
              onClick={handleTrigger}
              className="ml-2 underline"
              disabled={trigger.isPending}
            >
              Reîncearcă
            </button>
          </div>
        )}

        {status === 'none' && !isBusy && (
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-4 text-center">
            <p className="mb-2 text-sm text-gray-600">
              Niciun research încă pentru această întâlnire.
            </p>
            <button
              type="button"
              onClick={handleTrigger}
              disabled={trigger.isPending}
              className="text-fixed-white rounded bg-blue-600 px-3 py-1.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              Pornește research
            </button>
          </div>
        )}

        {status === 'done' && report && <ReportView report={report} />}
        {status === 'done' && !report && (
          <p className="text-sm text-gray-500">Research finalizat, dar raportul este gol.</p>
        )}
      </div>
    </div>
  );
}

/** A single upcoming-meeting row with its status badge and the Research/Info button. */
function MeetingRow({ meeting, onOpen }: { meeting: UpcomingMeeting; onOpen: () => void }) {
  const badge = STATUS_BADGE[meeting.researchStatus];
  const hasResearch = meeting.researchStatus === 'done';

  return (
    <li className="flex items-center justify-between gap-2 rounded-md border border-gray-200 p-3">
      <div className="min-w-0">
        <p className="truncate font-medium text-gray-900">{meeting.personName}</p>
        <p className="truncate text-xs text-gray-500">
          {formatTime(meeting.startTime)}
          {meeting.meetingDescription ? ` · ${meeting.meetingDescription}` : ''}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
          {badge.label}
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="text-fixed-white rounded bg-blue-600 px-3 py-1.5 text-xs font-medium hover:bg-blue-700"
        >
          {hasResearch ? 'Info' : 'Research'}
        </button>
      </div>
    </li>
  );
}

/**
 * Meeting Intelligence & Prep section (CLE-234). Lists upcoming "Meet [Nume]"
 * meetings detected from the user's calendar, each with a research-status badge
 * and a Research/Info button that opens the prep modal (triggers research,
 * polls while it runs, renders the full profile when ready).
 */
export function MeetingsSection({ userId, date }: Props) {
  const { data, isLoading, isError } = useUpcomingMeetings(userId, date);
  const [openEventId, setOpenEventId] = useState<string | null>(null);

  const meetings = data?.meetings ?? [];
  const openMeeting = meetings.find((m) => m.eventId === openEventId) ?? null;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold text-gray-800">Pregătire întâlniri</h2>
        {data && (
          <span className="text-xs text-gray-500">
            {data.total} {data.total === 1 ? 'întâlnire' : 'întâlniri'}
            {!data.webSearchEnabled ? ' · căutare web dezactivată' : ''}
          </span>
        )}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Se încarcă întâlnirile…</p>}
      {isError && <p className="text-sm text-red-600">Încărcarea întâlnirilor a eșuat.</p>}

      {!isLoading && !isError && meetings.length === 0 && (
        <p className="text-sm text-gray-500">Nicio întâlnire „Meet [Nume]” în acest interval.</p>
      )}

      {meetings.length > 0 && (
        <ul className="space-y-2">
          {meetings.map((m) => (
            <MeetingRow key={m.eventId} meeting={m} onOpen={() => setOpenEventId(m.eventId)} />
          ))}
        </ul>
      )}

      {openMeeting && <ResearchModal meeting={openMeeting} onClose={() => setOpenEventId(null)} />}
    </section>
  );
}
